import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./Footer.css";
// import '../../assets/css/Responsive/Header_Responsive.css'
import { fetchGlobalSettings } from "../../api/globalsetting";

const sanitizeString = (value) => {
    if (typeof value !== "string") return "";
    const trimmed = value.trim();
    return trimmed.length ? trimmed : "";
};

const sanitizeImage = (image) => {
    const url = sanitizeString(image?.url || "");
    if (!url) return null;
    return {
        url,
        alt: sanitizeString(image?.alt || ""),
    };
};

const sanitizeSocialIcons = (icons) => {
    if (!Array.isArray(icons)) return [];

    return icons
        .map((item) => {
            const icon = sanitizeImage(item?.icon);
            if (!icon) return null;
            return {
                url: sanitizeString(item?.url || ""),
                icon,
            };
        })
        .filter(Boolean);
};

function Footer() {
    const [footer, setFooter] = useState(null);

    useEffect(() => {
        let isMounted = true;

        fetchGlobalSettings()
            .then((res) => {
                if (!isMounted || !res?.footer) return;

                const apiFooter = res.footer;
                const sanitizedFooter = {
                    email: sanitizeString(apiFooter.email),
                    support_email: sanitizeString(apiFooter.support_email),
                    address: sanitizeString(apiFooter.address),
                    map_url: sanitizeString(apiFooter.map_url),
                    map_button_title: sanitizeString(apiFooter.map_button_title),
                    privacy_policy_url: sanitizeString(apiFooter.privacy_policy_url),
                    privacy_policy_text: sanitizeString(apiFooter.privacy_policy_text),
                    copyright: sanitizeString(apiFooter.copyright),
                    footer_text_above_copyright: sanitizeString(apiFooter.footer_text_above_copyright),
                    logo: sanitizeImage(apiFooter.logo),
                    map_image: sanitizeImage(apiFooter.map_image),
                    map_image_tablet: sanitizeImage(apiFooter.map_image_tablet),
                    map_image_mobile: sanitizeImage(apiFooter.map_image_mobile),
                    social_icons: sanitizeSocialIcons(apiFooter.social_icons),
                };

                setFooter(sanitizedFooter);
            })
            .catch((err) => { });

        return () => {
            isMounted = false;
        };
    }, []);

    const addressLines = useMemo(() => {
        if (!footer?.address) return [];
        return footer.address.split(/\r?\n/).filter(Boolean);
    }, [footer?.address]);

    // Helper function to parse email with <br /> tag
    const parseEmailWithBr = (emailString) => {
        if (!emailString) return null;
        // Clean up the string and split by <br /> tag (handles various formats)
        const cleaned = emailString.replace(/\r?\n/g, '').trim();
        // Match pattern like "ticketsupport<br />@vancouverlookout.com" or "info<br />@example.com"
        const brMatch = cleaned.match(/^(.+?)(?:<br\s*\/?>)(.+)$/i);
        if (brMatch) {
            const before = brMatch[1].trim();
            const after = brMatch[2].trim();
            // Combine to form full email (after might already have @ or might not)
            const fullEmail = after.startsWith('@')
                ? before + after
                : before + '@' + after;
            return {
                before: before,
                after: after,
                hasBr: true,
                fullEmail: fullEmail
            };
        }
        // If no <br /> tag, return the email as-is
        return {
            before: cleaned,
            after: null,
            hasBr: false,
            fullEmail: cleaned
        };
    };

    // Parse email to handle <br /> tag
    const emailParts = useMemo(() => {
        return parseEmailWithBr(footer?.email);
    }, [footer?.email]);

    // Parse support_email to handle <br /> tag
    const supportEmailParts = useMemo(() => {
        return parseEmailWithBr(footer?.support_email);
    }, [footer?.support_email]);

    if (!footer) return null;

    const hasEmail = Boolean(footer.email);
    const hasSupport = Boolean(footer.support_email);
    const hasAddress = addressLines.length > 0;
    const hasContactBlocks = hasEmail || hasSupport || hasAddress;
    const hasSocial = Array.isArray(footer.social_icons) && footer.social_icons.length > 0;
    const hasMapAssets = footer.map_url || footer.map_image?.url || footer.map_image_tablet?.url || footer.map_image_mobile?.url;
    const hasFooterBottom = footer.privacy_policy_text || footer.copyright || footer.footer_text_above_copyright;
    const hasAcknowledgment = Boolean(footer.footer_text_above_copyright);

    return (
        <footer className="footer">
            <div className="canvas">
                <div className="footer-card">
                    <div className="footer-inner">
                        <div className="footer-wrapper">

                            {/* LEFT SIDE */}
                            <div className="footer-left">

                                {/* Logo */}
                                {footer.logo?.url && (
                                    <div className="f-brand">
                                        <img
                                            src={footer.logo.url}
                                            alt={footer.logo.alt || "Footer Logo"}
                                        />
                                    </div>
                                )}

                                {/* Contact Blocks */}
                                {hasContactBlocks && (
                                    <div className="main-block">
                                        {hasEmail && emailParts && (
                                            <div className="f-block">
                                                <span className="f-label">EMAIL</span>
                                                <a
                                                    className="f-link support-email-wrapper"
                                                    href={`mailto:${emailParts.fullEmail}`}
                                                >
                                                    {emailParts.before}
                                                    {emailParts.hasBr && (
                                                        <>
                                                            <br className="support-email-br" />
                                                            {emailParts.after}
                                                        </>
                                                    )}
                                                    {!emailParts.hasBr && emailParts.after}
                                                </a>
                                            </div>
                                        )}

                                        {hasSupport && supportEmailParts && (
                                            <div className="f-block">
                                                <span className="f-label">SUPPORT</span>
                                                <a
                                                    className="f-link support-email-wrapper"
                                                    href={`mailto:${supportEmailParts.fullEmail}`}
                                                >
                                                    {supportEmailParts.before}
                                                    {supportEmailParts.hasBr && (
                                                        <>
                                                            <br className="support-email-br" />
                                                            {supportEmailParts.after}
                                                        </>
                                                    )}
                                                    {!supportEmailParts.hasBr && supportEmailParts.after}
                                                </a>
                                            </div>
                                        )}

                                        {hasAddress && (
                                            <div className="f-block">
                                                <span className="f-label">ADDRESS</span>
                                                <p className="f-text">
                                                    <a href={footer.map_url} target="_blank" rel="noopener noreferrer" className="f-text">
                                                        {addressLines.map((line, index) => (
                                                            <React.Fragment key={`${line}-${index}`}>
                                                                {line}
                                                                {index !== addressLines.length - 1 && <br />}
                                                            </React.Fragment>
                                                        ))}
                                                    </a>
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Social Icons */}
                                {/* {hasSocial && (
                                    <div className="f-social">
                                        {footer.social_icons.map((item, index) => (
                                            <a
                                                href={item.url || "#"}
                                                key={`${item.icon?.url || "icon"}-${index}`}
                                                target={item.url ? "_blank" : undefined}
                                                rel={item.url ? "noopener noreferrer" : undefined}
                                            >
                                                <img
                                                    src={item.icon?.url}
                                                    alt={item.icon?.alt || "Social Icon"}
                                                />
                                            </a>
                                        ))}
                                    </div>
                                )} */}
                                {hasSocial && (
                                    <div className="f-social">
                                        {footer.social_icons.map((item, index) => {
                                            let link = "#";

                                            if (index === 0) link = "https://www.instagram.com/vancouverlookout/";
                                            if (index === 1) link = "https://www.facebook.com/Vancouverlookout";

                                            return (
                                                <a
                                                    href={link}
                                                    key={`social-${index}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <img src={item.icon?.url} alt={item.icon?.alt || "Social Icon"} />
                                                </a>
                                            );
                                        })}
                                    </div>
                                )}

                            </div>

                            {/* RIGHT SIDE */}
                            {hasMapAssets && (
                                <div className="footer-right">
                                    <div className="map-wrapper">
                                        {footer.map_url && (
                                            <a
                                                href={footer.map_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="map-btn"
                                            >
                                                <span className="map-icon">
                                                    {/* Get Directions */}
                                                    {footer.map_button_title || "Get Directions"}

                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <rect width="24" height="24" rx="12" fill="#093E59" />
                                                        <path d="M16.8968 10.6854L17.2484 11.0409C17.343 10.9473 17.3964 10.8198 17.3968 10.6868C17.3972 10.5537 17.3445 10.4259 17.2504 10.3318L16.8968 10.6854ZM7.8374 16.2682C7.83743 16.5443 8.06132 16.7681 8.33746 16.7681C8.61361 16.7681 8.83743 16.5442 8.8374 16.268L8.3374 16.2681L7.8374 16.2682ZM14.0485 7.12997C13.8532 6.93471 13.5367 6.93471 13.3414 7.12997C13.1461 7.32523 13.1461 7.64182 13.3414 7.83708L13.695 7.48353L14.0485 7.12997ZM13.3433 13.4968C13.147 13.6909 13.1453 14.0075 13.3395 14.2039C13.5336 14.4002 13.8502 14.4019 14.0466 14.2077L13.695 13.8522L13.3433 13.4968ZM13.0805 10.6854L13.0805 10.1854L13.0805 10.6854ZM16.8968 10.6854L17.2504 10.3318L14.0485 7.12997L13.695 7.48353L13.3414 7.83708L16.5433 11.0389L16.8968 10.6854ZM16.8968 10.6854L16.5452 10.3299L13.3433 13.4968L13.695 13.8522L14.0466 14.2077L17.2484 11.0409L16.8968 10.6854ZM16.8968 10.6854L16.8968 10.1854L13.0805 10.1854L13.0805 10.6854L13.0805 11.1854L16.8968 11.1854L16.8968 10.6854ZM13.0805 10.6854L13.0805 10.1854C11.9841 10.1854 11.093 10.1843 10.3993 10.2769C9.69846 10.3705 9.10375 10.5702 8.67213 11.0477C8.25132 11.5133 8.05867 12.1642 7.95741 12.9786C7.85534 13.7993 7.83721 14.8768 7.8374 16.2682L8.3374 16.2681L8.8374 16.268C8.83721 14.8677 8.85657 13.8515 8.94976 13.102C9.04375 12.3462 9.20413 11.9504 9.41399 11.7183C9.61304 11.498 9.92665 11.3489 10.5316 11.2682C11.1438 11.1864 11.9561 11.1854 13.0805 11.1854L13.0805 10.6854Z" fill="white" />
                                                    </svg>

                                                </span>
                                            </a>
                                        )}

                                        {(footer.map_image?.url || footer.map_image_tablet?.url || footer.map_image_mobile?.url) && (
                                            footer.map_url ? (
                                                <a
                                                    href={footer.map_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="footer-map-link"
                                                >
                                                    <picture>
                                                        {/* Mobile: max-width 767px */}
                                                        {footer.map_image_mobile?.url && (
                                                            <source media="(max-width: 767px)" srcSet={footer.map_image_mobile.url} />
                                                        )}
                                                        {/* Tablet: 768px to 991px */}
                                                        {footer.map_image_tablet?.url && (
                                                            <source media="(min-width: 768px) and (max-width: 991px)" srcSet={footer.map_image_tablet.url} />
                                                        )}
                                                        {/* Desktop: 992px and above */}
                                                        {footer.map_image?.url && (
                                                            <source media="(min-width: 992px)" srcSet={footer.map_image.url} />
                                                        )}
                                                        {/* Fallback image - use desktop as default */}
                                                        <img
                                                            src={footer.map_image?.url || footer.map_image_tablet?.url || footer.map_image_mobile?.url}
                                                            alt={footer.map_image?.alt || footer.map_image_tablet?.alt || footer.map_image_mobile?.alt || "Footer Map"}
                                                            className="footer-map"
                                                        />
                                                    </picture>
                                                </a>
                                            ) : (
                                                <picture>
                                                    {/* Mobile: max-width 767px */}
                                                    {footer.map_image_mobile?.url && (
                                                        <source media="(max-width: 767px)" srcSet={footer.map_image_mobile.url} />
                                                    )}
                                                    {/* Tablet: 768px to 991px */}
                                                    {footer.map_image_tablet?.url && (
                                                        <source media="(min-width: 768px) and (max-width: 991px)" srcSet={footer.map_image_tablet.url} />
                                                    )}
                                                    {/* Desktop: 992px and above */}
                                                    {footer.map_image?.url && (
                                                        <source media="(min-width: 992px)" srcSet={footer.map_image.url} />
                                                    )}
                                                    {/* Fallback image - use desktop as default */}
                                                    <img
                                                        src={footer.map_image?.url || footer.map_image_tablet?.url || footer.map_image_mobile?.url}
                                                        alt={footer.map_image?.alt || footer.map_image_tablet?.alt || footer.map_image_mobile?.alt || "Footer Map"}
                                                        className="footer-map"
                                                    />
                                                </picture>
                                            )
                                        )}
                                    </div>
                                </div>
                            )}

                        </div>

                        {/* Bottom Section */}
                        {hasFooterBottom && (
                            <div className="footer-bottom">
                                <div className="footer-bottom-left">
                                    {(footer.privacy_policy_text || true) && (
                                        <Link to="/privacy-policy" className="f-legal">
                                            {footer.privacy_policy_text || "Privacy Policy"}
                                        </Link>
                                    )}
                                    {footer.copyright && (
                                        <span className="f-copy">
                                            {footer.copyright}
                                        </span>
                                    )}
                                </div>
                                {hasAcknowledgment && (
                                    <div
                                        className="footer-acknowledgment"
                                        dangerouslySetInnerHTML={{ __html: footer.footer_text_above_copyright }}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </footer>
    );
}

export default Footer;

